import { useState, useEffect, useCallback } from 'react';
import { useGetUserCustomizationQuery, useSaveUserCustomizationMutation } from '../api/userCustomizations';
import { getSessionVar, setSessionVar } from '../lib/helpers';
import { COLUMN_CONFIG } from '../pages/Discovery/Inventory/components/InventoryTopBar/constants';

const SESSION_STORAGE_KEY = 'user_customizations';

// Default user customizations for tables, sidebars, etc. Used if no customization has been set
export const DEFAULT_CUSTOMIZATION = {
    discovery: {
        inventory_table: {
            columns: ['ready_status', ...COLUMN_CONFIG.filter((c) => c.default).map((c) => c.key)],
            sorting: null,
        },
        move_groups_applications_sidebar: {
            move_groups: [],
            applications: [],
        },
        move_groups_applications_table: {
            columns: ['ready_status', ...COLUMN_CONFIG.filter((c) => c.default).map((c) => c.key)],
            sorting: null,
        },
    },
    notifications: {
        items: {},
    },
};

// Get customization for specific org from user customizations cache
const getOrgCustomization = (orgId) => {
    const allCustomizations = getSessionVar(SESSION_STORAGE_KEY) || {};
    return allCustomizations[orgId] || null;
};

// Save customization for specific org to user customizations cache
const saveOrgCustomization = (orgId, customization) => {
    const allCustomizations = getSessionVar(SESSION_STORAGE_KEY) || {};
    // Merge with existing customization to preserve other properties
    const existingCustomization = allCustomizations[orgId] || {};
    allCustomizations[orgId] = {
        ...existingCustomization,
        ...customization,
        discovery: {
            ...(existingCustomization.discovery || {}),
            ...(customization.discovery || {}),
        },
        notifications: {
            ...(existingCustomization.notifications || {}),
            ...(customization.notifications || {}),
            items: {
                ...((existingCustomization.notifications || {}).items || {}),
                ...((customization.notifications || {}).items || {}),
            },
        },
    };
    setSessionVar(SESSION_STORAGE_KEY, allCustomizations);
};

// Manage user customization with session storage and API in sync
export const useUserCustomization = () => {
    // Get user org ID
    const orgId = sessionStorage.getItem('userOrg');
    const [customization, setCustomization] = useState(() => {
        // Try to load from session storage first for this org
        if (orgId) {
            const sessionCustomization = getOrgCustomization(orgId);
            if (sessionCustomization) {
                return sessionCustomization;
            }
        }
        return DEFAULT_CUSTOMIZATION;
    });

    // This will be set back to false on unmount, if we navigate to a new route in the app.
    const [isInitialized, setIsInitialized] = useState(false);

    // Check if we have any cached customization for this org (default or not)
    const cachedCustomization = orgId ? getOrgCustomization(orgId) : null;
    const hasCachedCustomization = cachedCustomization !== null;

    // Fetch customization from API only if we don't have any cached customization
    const { data: apiCustomization, isLoading } = useGetUserCustomizationQuery(orgId, {
        skip: !orgId || hasCachedCustomization,
    });
    const [saveCustomizationToApi] = useSaveUserCustomizationMutation();

    // Reset initialization when orgId changes
    useEffect(() => {
        setIsInitialized(false);
    }, [orgId]);

    // Initialize customization from API if available
    // Note: when skip=true RTKQ still returns stale data from cache.
    // We only want to process API data when we actually make the request, not skipped.
    useEffect(() => {
        if (apiCustomization && orgId && !hasCachedCustomization) {
            // No cached customization, save API customization to session storage (always cache successful API responses)
            saveOrgCustomization(orgId, apiCustomization);
            if (!isInitialized) {
                setCustomization(apiCustomization);
                setIsInitialized(true);
            }
        } else if (!apiCustomization && !isLoading && !isInitialized && orgId && !hasCachedCustomization) {
            // No API customization and no session cache, save default customization
            saveOrgCustomization(orgId, DEFAULT_CUSTOMIZATION);
            saveCustomizationToApi({ orgId, customization: DEFAULT_CUSTOMIZATION })
                .unwrap()
                .catch((error) => {
                    console.error('Error saving default user customization to API:', error);
                });
            setIsInitialized(true);
        } else if (hasCachedCustomization && !isInitialized) {
            // Session cache exists and API was skipped - just mark as initialized and do not use stale API data
            setIsInitialized(true);
        }
    }, [apiCustomization, isLoading, isInitialized, orgId, hasCachedCustomization, saveCustomizationToApi]);

    // Save customization to both session storage and API
    const saveCustomization = useCallback(
        (newCustomization) => {
            // Update state
            setCustomization(newCustomization);
            // Save to session storage for this org
            if (orgId) {
                saveOrgCustomization(orgId, newCustomization);
            }
            // Save to API
            if (orgId) {
                saveCustomizationToApi({ orgId, customization: newCustomization })
                    .unwrap()
                    .catch((error) => {
                        console.error('Error saving user customization to API:', error);
                    });
            }
        },
        [orgId, saveCustomizationToApi],
    );

    // Update column order for specified table
    const updateColumnOrder = useCallback(
        (columnOrder, tableType = 'inventory_table') => {
            const newCustomization = {
                ...customization,
                discovery: {
                    ...customization.discovery,
                    [tableType]: {
                        ...customization.discovery[tableType],
                        columns: columnOrder,
                    },
                },
            };
            saveCustomization(newCustomization);
        },
        [customization, saveCustomization],
    );

    const updateSorting = useCallback(
        (field, direction, tableType = 'inventory_table') => {
            const newCustomization = {
                ...customization,
                discovery: {
                    ...customization.discovery,
                    [tableType]: {
                        ...customization.discovery[tableType],
                        sorting: field ? { field, direction } : null,
                    },
                },
            };
            saveCustomization(newCustomization);
        },
        [customization, saveCustomization],
    );

    // Update move groups and applications order
    const updateMoveGroupsApplicationsOrder = useCallback(
        (moveGroupsOrder, applicationsOrder) => {
            const newCustomization = {
                ...customization,
                discovery: {
                    ...customization.discovery,
                    move_groups_applications_sidebar: {
                        move_groups: moveGroupsOrder || [],
                        applications: applicationsOrder || [],
                    },
                },
            };
            saveCustomization(newCustomization);
        },
        [customization, saveCustomization],
    );

    // Get customization for specified table
    const getTableCustomization = useCallback(
        (tableType = 'inventory_table') => {
            return customization.discovery[tableType];
        },
        [customization],
    );

    // Save the full notifications.items object to user_customizations
    const saveNotificationItems = useCallback(
        (items) => {
            const newCustomization = {
                ...customization,
                notifications: {
                    ...(customization.notifications || {}),
                    items,
                },
            };
            saveCustomization(newCustomization);
        },
        [customization, saveCustomization],
    );

    return {
        customization: customization.discovery.inventory_table,
        moveGroupsApplicationsOrder: customization.discovery.move_groups_applications_sidebar,
        moveGroupsApplicationsTableCustomization: customization.discovery.move_groups_applications_table,
        notificationItems: (customization.notifications || {}).items || {},
        updateColumnOrder,
        updateSorting,
        updateMoveGroupsApplicationsOrder,
        getTableCustomization,
        saveNotificationItems,
        isLoading: isLoading || !isInitialized,
    };
};
